//******************************************************************************
/**
 * @fileoverview FetchFile LinePNG用ファイル取得クラスImageDecoder+createImageBitmap版
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @version 0.4.0
 * @since 2026-01-01
 * @copyright 2026 KITAO Kaoru
 * @license MIT
 * * @history
 * - 2026.03.06 v0.4.0 mime type 自動識別機能実装（png、webp専用）
 * - 2026.02.27 v0.3.1 package.json堅牢化
 * - 2026.02.25 v0.3.0 rgba配列でなくImageDataを返すよう変更
 * - 2026.02.25 v0.3.0 blobから直接createImageBitmapするよう変更
 * - 2026.02.21 v0.2.1 クラス名変更
 * - 2026.02.04 v0.2.0 コア部分を基底クラスとして分離
 * - 2026.01.01 v0.1.0 リリース
 */

//******************************************************************************
// import
import { FileFetcherBase } from "./FileFetcherBase.js";
import { detectMime } from "./detectMimeType.js";

//******************************************************************************
/**
 * ファイル取得（PNGデコード機能付き）のためのクラス
 */
class FileFetcher extends FileFetcherBase {
	//**************************************************************************
	/**
	 * PNGファイルパス指定でRGBAを取り出す
	 * @param {string} path PNGファイルへのパス
	 * @param {string} [mime] 画像ファイルのmimeType
	 * @returns {Promise<ImageData>} 取得結果
	 */
	async getImageData(path, mime) {
		//----------------------------------------------------------------------
		// ArrayBufferを取得
		const buffer = await this.fetcher.fetch(path);
		//----------------------------------------------------------------------
		// mime指定がない場合はmimeTypeを自動判定
		const mimeType = typeof mime !== "undefined" ? mime : detectMime(buffer);
		//----------------------------------------------------------------------
		// ImageDecoder装備の場合はここで処理
		if (typeof window.ImageDecoder !== "undefined") {
			// decoderを作ってデコード
			const decoder = new ImageDecoder({
				data: buffer,
				type: mimeType
			});
			const { image } = await decoder.decode();
			// 型付配列を準備
			const typed = new Uint8ClampedArray(
				image.displayWidth * image.displayHeight * 4
			);
			// デコード結果を型付配列にコピー
			await image.copyTo(typed, { format: "RGBA" });
			const width = image.displayWidth;
			// デコード結果を閉じる
			image.close();
			// 型付配列を返す
			// return typed;
			return new ImageData(typed, width);
		}
		//----------------------------------------------------------------------
		// ImageDecoder未装備の場合のフォールバック処理
		const blob = new Blob([buffer], { type: mime });
		const bitmap = await createImageBitmap(blob, {
			colorSpaceConversion: "none",
			premultiplyAlpha: "none"
		});
		const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
		const ctx = canvas.getContext("2d");
		ctx.drawImage(bitmap, 0, 0);
		bitmap.close();
		// return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
		return ctx.getImageData(0, 0, canvas.width, canvas.height);
	}
};

//******************************************************************************
// export
export { FileFetcher };
